/*
 * Third-party bottom-corner widgets (Shopify Inbox "Support" button and
 * similar) render inside a shadow root, so theme CSS cannot reach them and
 * z-index rules are scoped to their own stacking context. This script finds
 * those hosts and hides them while any theme overlay (drawer, modal, mobile
 * filter dialog) is open.
 */
(function () {
  var OVERLAY_CLASSES = [
    'drawer-open',
    'mobile-menu-open',
    'page-layout-sidebar-drawer-open',
    'overflow-hidden',
    'overflow-hidden-tablet',
  ];

  // Markers that identify the chat activator once we are inside a shadow
  // root. Deliberately narrow: #shopify-element-wrapper is a shared Shopify
  // wrapper also used by the consent banner, so matching on it hides the
  // banner too.
  var SHADOW_MARKERS = '.activator-nudge, [part="activator"]';

  // Never touch a host that also renders consent UI.
  var EXCLUDE_MARKERS =
    '#consent_cookie_banner, #consent_cookie_banner_container, [id*="consent"]';

  // Theme drawers/modals/sticky headers occupy 20-70, so sit below those
  // but comfortably above ordinary page content.
  var CHAT_Z = '10';

  // The Google "Store quality" FAB must sit under the consent banner, which
  // uses the maximum z-index. It may render in a shadow root or inside an
  // iframe, so match the button itself and any Google-owned iframe.
  var GOOGLE_BADGE =
    '.float-mv-button, [dialoglabel="Store quality by Google"],' +
    '[id*="merchantwidget"], [id*="merchant-widget"],' +
    'iframe[src*="merchantverse"], iframe[src*="merchantwidget"],' +
    'iframe[src*="gstatic.com"], iframe[src*="google.com"],' +
    'iframe[id*="merchant"], iframe[name*="merchant"],' +
    'iframe[title*="Store quality"]';
  var GOOGLE_Z = '2147483000';

  // The consent banner also renders in a shadow root, so raise it from JS
  // rather than relying on the stylesheet.
  var CONSENT =
    '#consent_cookie_banner_container, #consent_cookie_banner';
  var CONSENT_Z = '2147483647';

  var hosts = new Set();

  // Walk the document and every shadow root, since these widgets render
  // inside shadow DOM where ordinary selectors cannot reach them.
  function deepQueryAll(selector, root, out) {
    root = root || document;
    out = out || [];
    root.querySelectorAll('*').forEach(function (el) {
      if (el.matches && el.matches(selector)) out.push(el);
      if (el.shadowRoot) deepQueryAll(selector, el.shadowRoot, out);
    });
    return out;
  }

  function raiseConsentBanner() {
    deepQueryAll(CONSENT).forEach(function (el) {
      el.style.setProperty('z-index', CONSENT_Z, 'important');
      var root = el.getRootNode();
      if (root && root.host) {
        root.host.style.setProperty('position', 'relative', 'important');
        root.host.style.setProperty('z-index', CONSENT_Z, 'important');
      }
    });
  }

  var watched = new WeakSet();

  // Google may rewrite an element's style after we restack it, which drops
  // our override, so re-apply whenever its style attribute changes.
  function pinZ(el) {
    if (el.style.getPropertyValue('z-index') !== GOOGLE_Z) {
      el.style.setProperty('z-index', GOOGLE_Z, 'important');
    }
    if (watched.has(el)) return;
    watched.add(el);
    new MutationObserver(function () {
      if (el.style.getPropertyValue('z-index') !== GOOGLE_Z) {
        el.style.setProperty('z-index', GOOGLE_Z, 'important');
      }
    }).observe(el, { attributes: true, attributeFilter: ['style'] });
  }

  // Theme wrappers must never be restacked (an in-page Google embed such as
  // a map lives inside them).
  var THEME_WRAPPERS =
    'main, header, footer, .shopify-section, [id^="shopify-section"], #MainContent';

  // The element that decides the badge's stacking order can be any
  // positioned ancestor (a fixed wrapper with its own z-index, or the shadow
  // host), so lower every positioned ancestor from the badge up to <body>,
  // and make the outermost one a stacking context that caps the rest. Only
  // floating widgets qualify: something in the chain must be fixed.
  function capStacking(el) {
    var chain = [];
    var floating = false;
    var node = el;
    while (node && node !== document.body && node !== document.documentElement) {
      if (node.nodeType === 1) {
        if (node.closest && (node.closest(CONSENT) || node.matches(THEME_WRAPPERS))) return;
        if (getComputedStyle(node).position === 'fixed') floating = true;
        chain.push(node);
      }
      var parent = node.parentNode;
      node = parent && parent.host ? parent.host : parent;
    }
    if (!floating || !chain.length) return;

    chain.forEach(function (item) {
      var cs = getComputedStyle(item);
      if (cs.position !== 'static' || cs.zIndex !== 'auto') pinZ(item);
    });

    var outermost = chain[chain.length - 1];
    if (getComputedStyle(outermost).position === 'static') {
      outermost.style.setProperty('position', 'relative', 'important');
    }
    pinZ(outermost);
  }

  function lowerGoogleBadge() {
    deepQueryAll(GOOGLE_BADGE).forEach(capStacking);
  }

  function isWidgetHost(el) {
    if (!el.shadowRoot) return false;
    try {
      if (el.shadowRoot.querySelector(EXCLUDE_MARKERS)) return false;
      return !!el.shadowRoot.querySelector(SHADOW_MARKERS);
    } catch (e) {
      return false;
    }
  }

  function collectHosts() {
    var found = false;
    document.querySelectorAll('body > *').forEach(function (el) {
      if (hosts.has(el)) return;
      if (isWidgetHost(el)) {
        hosts.add(el);
        el.style.setProperty('z-index', CHAT_Z, 'important');
        found = true;
      }
    });
    return found;
  }

  function overlayOpen() {
    return OVERLAY_CLASSES.some(function (c) {
      return document.body.classList.contains(c);
    });
  }

  function apply() {
    var hide = overlayOpen();
    hosts.forEach(function (el) {
      if (hide) {
        el.style.setProperty('display', 'none', 'important');
      } else {
        el.style.removeProperty('display');
      }
    });
  }

  function sync() {
    collectHosts();
    lowerGoogleBadge();
    raiseConsentBanner();
    apply();
  }

  var queued = false;
  function scheduleSync() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      sync();
    });
  }

  // Widgets load asynchronously, so watch for late injection.
  new MutationObserver(scheduleSync).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Drawers toggle classes on <body>.
  new MutationObserver(apply).observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });

  sync();
})();
